import React, { useMemo, useState } from "react";
import _ from "lodash";
import { Accordion, Icon, Header, Label } from "semantic-ui-react";

export default function DailyUserCountCalendar({ data = {} }) {
  const [activeYear, setActiveYear] = useState(null);

  const years = useMemo(() => {
    return _(data)
      .toPairs()
      .map(([year, months]) => ({
        year: Number(year),
        months: _(months)
          .toPairs()
          .map(([month, days]) => ({
            month: Number(month),
            days,
          }))
          .sortBy("month")
          .value(),
      }))
      .sortBy("year")
      .value();
  }, [data]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const renderMonth = (year, month, days) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const firstDay = new Date(year, month - 1, 1).getDay();

    const cells = [];

    // Empty cells before month starts
    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <div
          key={`empty-${i}`}
          style={{
            minHeight: 70,
            border: "1px solid var(--off-black-5)",
          }}
        />
      );
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const count = days?.[String(day)]?.count;

      cells.push(
        <div
          key={day}
          style={{
            minHeight: 70,
            border: "1px solid var(--off-black-5)",
            padding: 6,
            position: "relative",
            background: count != null ? 'var(--off-black-3)' : "transparent",
            color: 'var(--off-white-5)'
          }}
        >
          {/* Day number */}
          <div
            style={{
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            {day}
          </div>

          {/* Count */}
          {count != null && (
            <div
              style={{
                textAlign: "center",
                fontSize: 24,
                fontWeight: "bold",
                marginTop: 8,
              }}
            >
              <Label color={'blue'} size="mini">{count}</Label>
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={`${year}-${month}`} style={{ marginBottom: 30 }}>
        <Header as="h3">
          {monthNames[month - 1]} {year}
        </Header>

        {/* Weekday headings */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
            (name) => (
              <div key={name} style={{ padding: 6 }}>
                {name}
              </div>
            )
          )}
        </div>

        {/* Calendar grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
          }}
        >
          {cells}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 20 }} className="app-dark-mode">
      <Header as="h2" dividing>
        Daily User Count
      </Header>

      <Accordion fluid styled>
        {years.map(({ year, months }, yearIdx) => (
          <React.Fragment key={year}>
            <Accordion.Title
              active={activeYear === yearIdx}
              onClick={() =>
                setActiveYear((prev) =>
                  prev === yearIdx ? null : yearIdx
                )
              }
            >
              <Icon name="dropdown" />
              {year}
            </Accordion.Title>

            <Accordion.Content active={activeYear === yearIdx}>
              {months.map(({ month, days }) =>
                renderMonth(year, month, days)
              )}
            </Accordion.Content>
          </React.Fragment>
        ))}
      </Accordion>
    </div>
  );
}